import { ContentContext, LocaleContext } from 'Components/hooks/context';
import React, { useContext, useEffect, useState } from 'react';

import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import SmartText from './SmartText';
import StackedAreaChart from '@reuters-graphics/chart-module-stacked-area-chart';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';

const atlas = new AtlasMetadataClient();

const areaChart = new StackedAreaChart();

const areaChartContainer = React.createRef();

const resize = debounce(() => {
  areaChart.draw();
}, 250);

const regionLinks = {};

const RegionShare = ({ counts, endDates }) => {
  const { cases, deaths } = counts;
  const content = useContext(ContentContext).template.byRegion;
  const locale = useContext(LocaleContext);
  const router = useRouter();
  const optionsText = content.showBy.split(' ');

  const [showAbsolute, setShowAbsolute] = useState(true);
  const [activeData, setActiveData] = useState('cases');

  const onNavigate = () => {
    window.scrollTo(0, 0);
    document.body.focus();
  };

  const getRegionLink = (slug) => () => {
    if (locale === 'en') {
      router.push('/regions/[region]', `/regions/${slug}`)
        .then(onNavigate);
    } else {
      router.push('/[locale]/regions/[region]', `/${locale}/regions/${slug}`)
        .then(onNavigate);
    }
  };

  atlas.regions.forEach(({ slug }) => {
    regionLinks[slug] = getRegionLink(slug);
  });

  useEffect(() => {
    const data = counts[activeData];

    areaChart
      .selection(areaChartContainer.current)
      .data(data)
      .props({
        locale,
        absolute: showAbsolute,
        margin: { left: 8, right: 42, top: 10, bottom: 20 },
        stroke: '#2f353f',
        stroke_width: 1,
        fills: ['#CF4A39', '#DE7040', '#E6934D', '#F6B368', '#F2D39E', '#ffffff'],
        highlight_variable: '',
        range: {
          start_date: '2020-02-02',
          end_date: endDates[activeData],
        },
        chart_formats: {
          // Format if number
          number: '~s',

          // Format if percent
          percent: '.0%',

          // Date format for the x axis
          date: '%b',
        },
        regionLinks,
      })
      .draw();
  }, [counts, cases, deaths, locale, showAbsolute, activeData, endDates]);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <div className={classnames(component)}>
      <div className='section-spacer before' />
      <div className='section-spacer-internal before' />
      <div>
        <h2>{content.title}</h2>
        <SmartText source={content.intro} cases={cases} locale={locale} />
        <div className='section-spacer-internal' />
        <div className='options font-sans'>
          <span>{optionsText[0]}</span>
          <div className='options-group'>
            <button
              className={classnames({ active: activeData === 'cases' })}
              onClick={() => setActiveData('cases')}
            >
              {content.showByCases}
            </button>
            <button
              className={classnames({ active: activeData === 'deaths' })}
              onClick={() => setActiveData('deaths')}
            >
              {content.showByDeaths}
            </button>
          </div>
          <div className='section-spacer-internal' />
          <span>{optionsText[1]}</span>
          <div className='options-group'>
            <button
              className={classnames({ active: showAbsolute })}
              onClick={() => setShowAbsolute(true)}
            >
              {content.showByCount}
            </button>
            <button
              className={classnames({ active: !showAbsolute })}
              onClick={() => setShowAbsolute(false)}
            >
              {content.showByPercent}
            </button>
          </div>
        </div>
        <div
          className={classnames(
            'stacked-area-chart-container font-sans',
            { absolute: showAbsolute }
          )} ref={areaChartContainer}
        />
        <p className='small'>*{content.note}</p>
      </div>
    </div>
  );
};

export default RegionShare;
